import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast, type ToastType } from '../providers/ToastProvider';
import {
    reviewDmPatientMessages,
    type DmPatientMessagesReviewResult
} from '../services/dmMessagesInbox';
import { apiErrorMessage } from '../utils/apiError';
import { plural } from '../utils/plural';
import { DM_MESSAGES_INBOX_KEY } from './queryKeys';
import { invalidateSidebarCounts } from './useSidebarCounts';

function alreadyInDmNote(count: number): string {
    return count > 0 ? ` (${count.toLocaleString()} already reviewed in DM)` : '';
}

function reviewMessagesToast(result: DmPatientMessagesReviewResult): {
    message: string;
    type: ToastType;
} {
    const { reviewed, alreadyResolvedInDm, failed } = result;
    const marked = `Marked ${plural(reviewed, 'message')} reviewed`;
    if (failed > 0) {
        return {
            message: `${marked}, ${failed.toLocaleString()} failed. Try again.`,
            type: 'error'
        };
    }
    if (reviewed === 0 && alreadyResolvedInDm === 0) {
        return { message: 'Nothing left to review', type: 'success' };
    }
    return { message: `${marked}${alreadyInDmNote(alreadyResolvedInDm)}`, type: 'success' };
}

interface ReviewDmPatientMessagesOptions {
    // Awaited before the refetch, so the list can't drop the row mid exit animation.
    onReviewed?: (patientId: string) => Promise<void>;
}

export function useReviewDmPatientMessages({ onReviewed }: ReviewDmPatientMessagesOptions = {}) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation({
        mutationFn: (patientId: string) => reviewDmPatientMessages(patientId),
        onSuccess: async (result, patientId) => {
            const toast = reviewMessagesToast(result);
            showToast(toast.message, toast.type);
            // A partial failure leaves the patient listed, so its row must not animate out.
            if (result.failed === 0) await onReviewed?.(patientId);
        },
        onError: (error) => {
            showToast(apiErrorMessage(error, 'Failed to mark the messages as reviewed'), 'error');
        },
        onSettled: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: [DM_MESSAGES_INBOX_KEY] }),
                invalidateSidebarCounts(queryClient)
            ])
    });
}
